<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="$t('ncrm.leads.board.filters.title')"
    :subtitle="$t('ncrm.leads.board.filters.subtitle')"
    :loading="fieldsLoading"
    :save-disabled="applyDisabled"
    :save-label="$t('ncrm.leads.board.filters.actions.apply')"
    :extra-action-label="hasDraftRules ? $t('ncrm.leads.board.filters.actions.clearAll') : ''"
    extra-action-color="secondary"
    :max-width="920"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="emitApply"
    @extra-action="clearAndApply"
  >
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
      <v-select
        v-model="draft.logic"
        :items="logicOptions"
        item-title="label"
        item-value="value"
        :label="$t('ncrm.leads.board.filters.logicLabel')"
        :menu-props="selectMenuProps"
        class="ncrm-lead-board-filters__logic"
      />
      <v-btn
        size="small"
        variant="tonal"
        color="primary"
        prepend-icon="mdi-plus"
        :disabled="fieldsLoading || !fieldOptions.length"
        @click="addRule"
      >
        {{ $t("ncrm.leads.board.filters.actions.addRule") }}
      </v-btn>
    </div>

    <v-alert
      v-if="!fieldOptions.length && !fieldsLoading"
      type="info"
      density="comfortable"
      class="mb-3"
    >
      {{ $t("ncrm.leads.board.filters.emptyFields") }}
    </v-alert>

    <v-alert
      v-else-if="hasIncompleteRules"
      type="warning"
      density="comfortable"
      class="mb-3"
    >
      {{ $t("ncrm.leads.board.filters.incomplete") }}
    </v-alert>

    <div class="d-flex flex-column ga-3">
      <v-card
        v-for="(rule, index) in draft.rules"
        :key="rule._uid"
        variant="outlined"
        class="pa-3"
      >
        <div class="d-flex align-center justify-space-between ga-2 mb-2">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.leads.board.filters.ruleIndex", { index: index + 1 }) }}
          </div>
          <v-btn
            icon="mdi-close"
            size="x-small"
            variant="text"
            color="error"
            :disabled="draft.rules.length <= 1"
            @click="removeRule(index)"
          />
        </div>

        <v-row>
          <v-col cols="12" md="5">
            <v-autocomplete
              v-model="rule.field_key"
              :items="fieldOptions"
              item-title="label"
              item-value="key"
              :label="$t('ncrm.leads.board.filters.fields.field')"
              :menu-props="selectMenuProps"
              clearable
              :loading="fieldsLoading"
              @update:model-value="onRuleFieldChanged(rule)"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="rule.operator"
              :items="operatorOptions"
              item-title="label"
              item-value="value"
              :label="$t('ncrm.leads.board.filters.fields.operator')"
              :menu-props="selectMenuProps"
              @update:model-value="onRuleOperatorChanged(rule)"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-combobox
              v-model="rule.value"
              :items="rule.value_suggestions"
              :label="$t('ncrm.leads.board.filters.fields.value')"
              :menu-props="selectMenuProps"
              :loading="rule.value_loading"
              clearable
              @focus="ensureSuggestionsLoaded(rule)"
              @update:search="(search) => scheduleValueSearch(rule, search)"
            />
          </v-col>
        </v-row>
      </v-card>
    </div>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  LEAD_BOARD_FILTER_LOGIC_VALUES,
  LEAD_BOARD_FILTER_OPERATOR_VALUES,
  normalizeLeadBoardFilterConfig,
} from "@/Utile/ncrmLeadBoardFilters.mjs";

const makeUid = () => `lead-filter-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;

export default {
  name: "NcrmLeadBoardFiltersDrawer",
  components: {
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    boardId: {
      type: [Number, String],
      default: null,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue", "apply"],
  data() {
    return {
      draft: this.createDraftFromFilters(this.filters),
      availableFields: [],
      fieldsLoading: false,
      fieldsRequestSeq: 0,
      selectMenuProps: {
        maxHeight: 320,
        contentClass: "ncrm-lead-board-select-menu",
        zIndex: 2710,
      },
    };
  },
  computed: {
    logicOptions() {
      return LEAD_BOARD_FILTER_LOGIC_VALUES.map((value) => ({
        value,
        label: this.$t(`ncrm.leads.board.filters.logic.${value}`),
      }));
    },
    operatorOptions() {
      return LEAD_BOARD_FILTER_OPERATOR_VALUES.map((value) => ({
        value,
        label: this.$t(`ncrm.leads.board.filters.operators.${value}`),
      }));
    },
    fieldOptions() {
      const byKey = new Map();
      for (const field of Array.isArray(this.availableFields) ? this.availableFields : []) {
        if (!field?.key) continue;
        byKey.set(field.key, field);
      }
      for (const rule of this.draft.rules || []) {
        if (!rule?.field_key) continue;
        if (byKey.has(rule.field_key)) continue;
        byKey.set(rule.field_key, {
          key: rule.field_key,
          label: rule.field_label || rule.field_key,
          group: rule.field_group || "",
        });
      }
      return Array.from(byKey.values());
    },
    hasDraftRules() {
      return (this.draft.rules || []).length > 0;
    },
    hasIncompleteRules() {
      return (this.draft.rules || []).some((rule) => !rule.field_key || !String(rule.value || "").trim());
    },
    applyDisabled() {
      return this.fieldsLoading || !this.hasDraftRules || this.hasIncompleteRules;
    },
  },
  watch: {
    modelValue(value) {
      if (!value) {
        this.clearRuleTimers();
        return;
      }
      this.draft = this.createDraftFromFilters(this.filters);
      this.loadFields();
    },
    filters: {
      deep: true,
      handler(value) {
        if (!this.modelValue) {
          this.draft = this.createDraftFromFilters(value);
        }
      },
    },
    boardId() {
      this.availableFields = [];
      if (this.modelValue) {
        this.loadFields();
      }
    },
  },
  beforeUnmount() {
    this.clearRuleTimers();
  },
  methods: {
    createRule(source = {}) {
      return {
        _uid: makeUid(),
        field_key: String(source.field_key || ""),
        field_label: String(source.field_label || ""),
        field_group: String(source.field_group || ""),
        operator: String(source.operator || "equals"),
        value: String(source.value || ""),
        value_suggestions: [],
        value_loading: false,
        value_request_seq: 0,
        search_debounce_id: null,
      };
    },
    createDraftFromFilters(filters) {
      const normalized = normalizeLeadBoardFilterConfig(filters);
      const rules = normalized.rules.length
        ? normalized.rules.map((rule) => this.createRule(rule))
        : [this.createRule()];
      return {
        logic: normalized.logic,
        rules,
      };
    },
    clearRuleTimers() {
      for (const rule of this.draft.rules || []) {
        if (rule?.search_debounce_id) {
          clearTimeout(rule.search_debounce_id);
          rule.search_debounce_id = null;
        }
      }
    },
    addRule() {
      this.draft.rules.push(this.createRule());
    },
    removeRule(index) {
      if (this.draft.rules.length <= 1) return;
      const rule = this.draft.rules[index];
      if (rule?.search_debounce_id) {
        clearTimeout(rule.search_debounce_id);
      }
      this.draft.rules.splice(index, 1);
    },
    findFieldByKey(fieldKey) {
      return this.fieldOptions.find((field) => field.key === fieldKey) || null;
    },
    onRuleFieldChanged(rule) {
      const selectedField = this.findFieldByKey(rule.field_key);
      rule.field_label = selectedField?.label || "";
      rule.field_group = selectedField?.group || "";
      rule.value = "";
      rule.value_suggestions = [];
      rule.value_request_seq = 0;
      if (!rule.field_key) return;
      this.fetchValueSuggestions(rule, "");
    },
    onRuleOperatorChanged(rule) {
      rule.value_suggestions = [];
      rule.value_request_seq = 0;
      if (!rule.field_key) return;
      this.fetchValueSuggestions(rule, "");
    },
    ensureSuggestionsLoaded(rule) {
      if (!rule?.field_key) return;
      if (Array.isArray(rule.value_suggestions) && rule.value_suggestions.length > 0) return;
      this.fetchValueSuggestions(rule, "");
    },
    scheduleValueSearch(rule, search) {
      if (!rule?.field_key) return;
      if (rule.search_debounce_id) {
        clearTimeout(rule.search_debounce_id);
      }
      rule.search_debounce_id = setTimeout(() => {
        this.fetchValueSuggestions(rule, search);
      }, 220);
    },
    async loadFields() {
      if (!this.boardId) return;
      const requestSeq = Number(this.fieldsRequestSeq || 0) + 1;
      this.fieldsRequestSeq = requestSeq;
      this.fieldsLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/advanced-filter-fields`);
        if (requestSeq !== Number(this.fieldsRequestSeq)) return;
        this.availableFields = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm-leads] load advanced filter fields failed:", error);
        if (requestSeq !== Number(this.fieldsRequestSeq)) return;
        this.availableFields = [];
      } finally {
        if (requestSeq === Number(this.fieldsRequestSeq)) {
          this.fieldsLoading = false;
        }
      }
    },
    async fetchValueSuggestions(rule, search = "") {
      if (!this.boardId || !rule?.field_key) return;
      const requestSeq = Number(rule.value_request_seq || 0) + 1;
      rule.value_request_seq = requestSeq;
      rule.value_loading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/advanced-filter-values`, {
          params: {
            fieldKey: rule.field_key,
            operator: rule.operator || "equals",
            search: String(search || "").trim() || undefined,
            limit: 20,
          },
        });
        if (requestSeq !== Number(rule.value_request_seq)) return;
        rule.value_suggestions = (Array.isArray(data?.items) ? data.items : []).filter(Boolean);
      } catch (error) {
        console.error("[ncrm-leads] load advanced filter values failed:", error);
        if (requestSeq !== Number(rule.value_request_seq)) return;
        rule.value_suggestions = [];
      } finally {
        if (requestSeq === Number(rule.value_request_seq)) {
          rule.value_loading = false;
        }
      }
    },
    buildPayload() {
      return normalizeLeadBoardFilterConfig({
        logic: this.draft.logic,
        rules: (this.draft.rules || []).map((rule) => ({
          field_key: rule.field_key,
          field_label: rule.field_label,
          field_group: rule.field_group,
          operator: rule.operator,
          value: rule.value,
        })),
      });
    },
    emitApply() {
      if (this.applyDisabled) return;
      this.$emit("apply", this.buildPayload());
      this.$emit("update:modelValue", false);
    },
    clearAndApply() {
      const emptyPayload = normalizeLeadBoardFilterConfig();
      this.draft = this.createDraftFromFilters(emptyPayload);
      this.$emit("apply", emptyPayload);
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.ncrm-lead-board-filters__logic {
  min-width: 220px;
  max-width: 280px;
}
</style>
